import React from "react";
import { Button } from "reactstrap";
import Select from "react-select";
import NavHeader from "./NavHeader";
import { genId, nuevoObjectId } from "./ids";
import { resolverFixture, UMBRAL_PRESELECCION } from "./importFixture";
import { etiquetaFecha } from "./fechas";

// Previsualización de un fixture importado desde Excel, antes de tocar el torneo.
//
// El archivo trae los partidos de todas las zonas de la jornada: acá se muestran
// sólo los que corresponden a esta zona (los demás se cuentan pero no se listan).
// Los partidos que quedan a medio reconocer los resuelve el usuario a mano.
export default function ImportFixtureScreen({
  filas = [],
  date,
  numero,
  zoneTeamIds = [],
  teams = [],
  onConfirm,
  onBack,
}) {
  const resolucion = React.useMemo(
    () => resolverFixture(filas, zoneTeamIds, teams),
    [filas, zoneTeamIds, teams]
  );

  // Resolución manual de las filas ambiguas: { [claveDelPartido]: teamId | "omitir" }.
  // Se indexa por `clave` y no por el número de fila del Excel, que se repite entre los
  // partidos que están uno al lado del otro en el cuadro (un bloque de columnas por cancha).
  // Las sugerencias fuertes vienen ya elegidas; las dudosas se muestran pero se dejan
  // sin elegir, para que el usuario tenga que decidir.
  const [ajustes, setAjustes] = React.useState({});
  React.useEffect(() => {
    setAjustes(
      Object.fromEntries(
        resolucion.ambiguas
          .filter((a) => a.sugerencia && a.sugerencia.score >= UMBRAL_PRESELECCION)
          .map((a) => [a.clave, a.sugerencia.id])
      )
    );
  }, [resolucion]);

  // Para cada cruce repetido, qué variante se importa: { [claveDelCruce]: claveDelPartido | "omitir" }.
  // Arranca en la primera variante, que es la que no tiene horario (el walkover).
  const [elegidos, setElegidos] = React.useState({});
  React.useEffect(() => {
    setElegidos(
      Object.fromEntries(resolucion.repetidos.map((r) => [r.clave, r.variantes[0].clave]))
    );
  }, [resolucion]);

  const teamName = (teamId) => {
    const t = teams.find((x) => x.id === teamId);
    return t ? t.name : "—";
  };

  // Una ambigua se vuelve importable cuando el usuario elige el equipo que falta.
  const ambiguasResueltas = resolucion.ambiguas
    .map((amb) => {
      const elegido = ajustes[amb.clave];
      if (!elegido || elegido === "omitir") return null;
      const esLocalElQueFalta = !amb.homeTeamId;
      const homeTeamId = esLocalElQueFalta ? elegido : amb.homeTeamId;
      const awayTeamId = esLocalElQueFalta ? amb.awayTeamId : elegido;
      if (homeTeamId === awayTeamId) return null;
      return { ...amb, homeTeamId, awayTeamId };
    })
    .filter(Boolean);

  const repetidosElegidos = resolucion.repetidos
    .map((r) => r.variantes.find((v) => v.clave === elegidos[r.clave]))
    .filter(Boolean);

  const aImportar = [...resolucion.importables, ...ambiguasResueltas, ...repetidosElegidos].sort((a, b) => {
    if (!a.hora) return 1;
    if (!b.hora) return -1;
    return a.hora.localeCompare(b.hora);
  });

  const existentes = (date && date.matches) || [];
  const jugados = existentes.filter((m) => m.jugado);

  // Un doble toque en el botón importaba dos veces: se duplicaban los partidos y
  // se disparaban dos avisos encimados (que dejaban el velo pegado en pantalla).
  const [confirmando, setConfirmando] = React.useState(false);
  const confirmadoRef = React.useRef(false);

  const confirmar = () => {
    if (confirmadoRef.current) return;
    confirmadoRef.current = true;
    setConfirmando(true);
    const matches = aImportar.map((p) => ({
      id: genId("match"),
      _id: nuevoObjectId(), // ver ids.js: el resultado se carga aparte, de a un partido
      homeTeamId: p.homeTeamId,
      awayTeamId: p.awayTeamId,
      hora: p.hora || "",
      cancha: p.cancha || "",
      wo: "",
      homeGoals: [],
      awayGoals: [],
    }));
    onConfirm(matches);
  };

  const subtitulo = [etiquetaFecha(date, numero, "corto"), filas.length + " filas leídas"]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="screen-slide">
      <NavHeader title="Importar Partidos" subtitle={subtitulo} onBack={onBack} />

      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="sub-section-title">Resumen</div>
          <ul className="mb-0 pl-3" style={{ fontSize: "0.875rem" }}>
            <li>
              <strong>{aImportar.length}</strong> partidos para esta zona.
            </li>
            {resolucion.ajenas.length > 0 && (
              <li className="text-muted">
                {resolucion.ajenas.length} partidos de otras zonas (se ignoran).
              </li>
            )}
            {resolucion.repetidos.length > 0 && (
              <li className="text-warning">
                {resolucion.repetidos.length} cruces que figuran más de una vez.
              </li>
            )}
            {resolucion.ambiguas.length > 0 && (
              <li className="text-warning">
                {resolucion.ambiguas.length} partidos con un equipo sin reconocer.
              </li>
            )}
            {resolucion.invalidas.length > 0 && (
              <li className="text-danger">{resolucion.invalidas.length} filas con error.</li>
            )}
          </ul>
        </div>
      </div>

      {existentes.length > 0 && (
        <div className={"card mb-3 border-" + (jugados.length > 0 ? "danger" : "warning")}>
          <div className="card-body py-3">
            <div className="font-weight-bold mb-1" style={{ fontSize: "0.875rem" }}>
              Esta fecha ya tiene {existentes.length} partidos cargados
            </div>
            <p className="mb-0 text-muted" style={{ fontSize: "0.8125rem" }}>
              Al importar se reemplazan por los del archivo.
              {jugados.length > 0 && (
                <span className="text-danger font-weight-bold">
                  {" "}
                  Atención: {jugados.length} ya tienen resultado cargado y se van a perder.
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {resolucion.repetidos.length > 0 && (
        <div className="mb-3">
          <div className="sub-section-title">Cruces repetidos</div>
          <p className="text-muted" style={{ fontSize: "0.8125rem" }}>
            Estos partidos figuran más de una vez en el cuadro, con datos distintos. Suele pasar
            cuando un equipo se da de baja: queda el partido programado y más abajo el walkover sin
            horario. Elegí cuál vale.
          </p>
          {resolucion.repetidos.map((rep) => (
            <div className="card mb-2" key={rep.clave}>
              <div className="card-body py-2">
                <div className="font-weight-bold mb-2" style={{ fontSize: "0.8125rem" }}>
                  {teamName(rep.variantes[0].homeTeamId)} vs {teamName(rep.variantes[0].awayTeamId)}
                </div>
                {rep.variantes.map((v) => (
                  <label
                    key={v.clave}
                    className="d-flex align-items-center mb-1"
                    style={{ fontSize: "0.8125rem", gap: "0.5rem", cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      name={"rep_" + rep.clave}
                      checked={elegidos[rep.clave] === v.clave}
                      onChange={() => setElegidos((prev) => ({ ...prev, [rep.clave]: v.clave }))}
                    />
                    <span>
                      {v.hora ? v.hora + (v.cancha ? " · Cancha " + v.cancha : "") : "Sin horario (walkover)"}
                      <span className="text-muted"> — fila {v.fila}</span>
                    </span>
                  </label>
                ))}
                <label
                  className="d-flex align-items-center mb-0"
                  style={{ fontSize: "0.8125rem", gap: "0.5rem", cursor: "pointer" }}
                >
                  <input
                    type="radio"
                    name={"rep_" + rep.clave}
                    checked={elegidos[rep.clave] === "omitir"}
                    onChange={() => setElegidos((prev) => ({ ...prev, [rep.clave]: "omitir" }))}
                  />
                  <span className="text-muted">No importar este partido</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {resolucion.ambiguas.length > 0 && (
        <div className="mb-3">
          <div className="sub-section-title">Equipos sin reconocer</div>
          <p className="text-muted" style={{ fontSize: "0.8125rem" }}>
            Estos partidos tienen un solo equipo de esta zona. Puede ser un error de tipeo en la
            planilla. Elegí el equipo que corresponde o dejalos sin importar.
          </p>
          {resolucion.ambiguas.map((amb) => {
            const esLocalElQueFalta = !amb.homeTeamId;
            const reconocido = esLocalElQueFalta ? amb.awayTeamId : amb.homeTeamId;
            const textoCrudo = esLocalElQueFalta ? amb.nombreLocal : amb.nombreVisitante;
            const elegido = ajustes[amb.clave];
            // Ordenadas por parecido: el candidato más probable queda arriba de todo.
            const opcionesAmbigua = amb.ranking.map((c) => ({
              value: c.id,
              label: amb.sugerencia && amb.sugerencia.id === c.id ? c.nombre + " (sugerido)" : c.nombre,
            }));
            return (
              <div className="card mb-2" key={amb.clave}>
                <div className="card-body py-2">
                  <div className="d-flex justify-content-between" style={{ fontSize: "0.8125rem" }}>
                    <span className="font-weight-bold">
                      {amb.nombreLocal} vs {amb.nombreVisitante}
                    </span>
                    <span className="text-muted">Fila {amb.fila}</span>
                  </div>
                  <p className="text-muted mb-2" style={{ fontSize: "0.8125rem" }}>
                    No se reconoce <strong>&quot;{textoCrudo}&quot;</strong> como{" "}
                    {esLocalElQueFalta ? "local" : "visitante"}. El otro equipo es{" "}
                    {teamName(reconocido)}.
                    {amb.sugerencia && (
                      <span className="d-block mt-1">
                        Se parece a <strong>{amb.sugerencia.nombre}</strong>
                        {amb.sugerencia.score < UMBRAL_PRESELECCION && " — confirmá si es el mismo equipo."}
                      </span>
                    )}
                  </p>
                  <Select
                    isSearchable={true}
                    isClearable={true}
                    placeholder="Elegí el equipo..."
                    classNamePrefix="react-select"
                    value={opcionesAmbigua.find((o) => o.value === elegido) || null}
                    onChange={(op) =>
                      setAjustes((prev) => ({ ...prev, [amb.clave]: op ? op.value : undefined }))
                    }
                    options={opcionesAmbigua}
                    noOptionsMessage={() => "Sin equipos"}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {resolucion.invalidas.length > 0 && (
        <div className="mb-3">
          <div className="sub-section-title">Filas con error</div>
          {resolucion.invalidas.map((inv, i) => (
            <p key={i} className="text-danger mb-1" style={{ fontSize: "0.8125rem" }}>
              Fila {inv.fila}: {inv.motivo}
            </p>
          ))}
        </div>
      )}

      <div className="sub-section-title">Partidos a importar</div>
      {aImportar.length === 0 ? (
        <p className="text-muted text-center py-2" style={{ fontSize: "0.875rem" }}>
          No hay partidos de esta zona en el archivo. Revisá que los nombres de los equipos
          coincidan con los cargados en el sistema.
        </p>
      ) : (
        aImportar.map((p) => (
          <div key={p.clave} className="drill-row drill-row--match match-row">
            <div className="match-row-main">
              <span className="match-row-hora">{p.hora || "—"}</span>
              <span className="match-row-teams">
                {teamName(p.homeTeamId)} vs {teamName(p.awayTeamId)}
              </span>
              <span className="match-row-cancha">{p.cancha ? "Cancha " + p.cancha : "—"}</span>
            </div>
          </div>
        ))
      )}

      <div className="d-flex justify-content-end mt-3" style={{ gap: "0.5rem" }}>
        <Button className="btn-arhock btn-sm" style={{ opacity: 0.55 }} onClick={onBack}>
          Cancelar
        </Button>
        <Button
          className="btn-arhock btn-sm"
          onClick={confirmar}
          disabled={aImportar.length === 0 || confirmando}
        >
          {confirmando ? "Importando…" : "Importar " + aImportar.length + " partidos"}
        </Button>
      </div>
    </div>
  );
}
